<script lang="ts" setup>
import { computed, ref } from 'vue';
import { NIcon, NProgress } from 'naive-ui';
import { ChevronDown16Regular, ChevronUp16Regular, RecordStop16Regular, SpinnerIos20Regular } from '@vicons/fluent';
import { useI18n } from 'vue-i18n';
import { usePlanRunStore } from '@/stores/trajectory/plan-run-store';
import { usePlanRunProgress } from '@/composables/use-plan-run-progress';
import { useMessageHandler } from '@/helpers/error/useMessageHandler';
import { CancelPlanRunOutcome } from '@/models/plan-run/plan-run';
import { DialogType } from '@/constants/dialog-type';
import ConfirmationModal from '@/components/shared/ConfirmationModal.vue';
import TooltipButton from '@/components/shared/TooltipButton.vue';
import '@/assets/scss/variables/_variables.css';

const { t } = useI18n();
const planRunStore = usePlanRunStore();

const baseKey = 'components.transport.dayplanner.plan_run.cancel';

const { percentage, phaseLabelKey, elapsedMinutes, elapsedSeconds } = usePlanRunProgress();

/**
 * Verstreken tijd, niet resterende: APE geeft geen voortgangsmaat en de looptijd van een run is niet te
 * voorspellen. Minuten en seconden apart vertaald, zodat "1 minuut en 1 seconde" in elke taal klopt.
 */
const elapsedText = computed(() => {
  const seconds = t('core.time.second_count', { count: elapsedSeconds.value });

  if (elapsedMinutes.value === 0) {
    return t('components.transport.dayplanner.plan_run.progress.elapsed_seconds', { seconds });
  }

  return t('components.transport.dayplanner.plan_run.progress.elapsed', {
    minutes: t('core.time.minute_count', { count: elapsedMinutes.value }),
    seconds,
  });
});

/**
 * Afbreken vraagt eerst om bevestiging, in dezelfde dialoog als de rest van de app (ConfirmationModal, type
 * Delete: rode bevestigknop). De inhoud benoemt de gevolgen — de berekening stopt, er komt geen planning uit,
 * de trajecten blijven staan — en dat het niet te hervatten is. Een popconfirm zou hier te licht zijn: een run
 * duurt minuten en is definitief weg.
 */
const showCancelConfirmation = ref(false);

/** Het aantal trajecten van de lopende run; de bevestiging noemt het. */
const trajectoryCount = computed(() => planRunStore.activeRun?.trajectoryIds.length ?? 0);

/**
 * De run is van een collega en we weten wie. Ontbreekt de naam — een verwijderde gebruiker — dan blijft de
 * regel weg: "gestart door" zonder naam voegt niets toe.
 */
const startedByOtherPlanner = computed(() => {
  const run = planRunStore.activeRun;

  return run !== null && !run.startedByMe && !!run.createdByName;
});

const cancel = async () => {
  const messageHandler = useMessageHandler();
  const planRunId = planRunStore.activeRun?.id;

  if (planRunId === undefined) {
    showCancelConfirmation.value = false;
    return;
  }

  const response = await planRunStore.cancelRun(planRunId);
  showCancelConfirmation.value = false;

  // De aanroep zelf mislukte. De run loopt dan gewoon door — dat is wat de planner moet weten, samen met wat
  // hij eraan kan doen. Geen foutcode: er is niets technisch aan te doen behalve het nog eens proberen.
  if (response === null) {
    messageHandler.showNotification(`${baseKey}.failed_title`, `${baseKey}.failed_description`, 'error');
    return;
  }

  if (response.outcome === CancelPlanRunOutcome.AlreadyFinished) {
    // Net te laat: de run rondde op eigen kracht af terwijl de bevestiging openstond. De uitkomst die er nu
    // ligt komt langs de gewone weg binnen — de poller ziet hem bij de eerstvolgende ronde en meldt hem.
    messageHandler.showNotification(`${baseKey}.too_late_title`, `${baseKey}.too_late_description`, 'info');
    return;
  }

  if (response.outcome === CancelPlanRunOutcome.ResultBeingApplied) {
    // De verwerking tot concept-transporten was al begonnen toen de bevestiging openstond; de server weigert
    // dan (7405: uit een afgebroken run mogen geen concepten ontstaan). De run maakt zijn werk af en de
    // planner kan de concepten daarna gewoon verwerpen.
    messageHandler.showNotification(`${baseKey}.applying_title`, `${baseKey}.applying_description`, 'info');
    return;
  }

  messageHandler.showNotification(`${baseKey}.stopped_title`, `${baseKey}.stopped_description`, 'info');
};
</script>

<template>
<!--
  Samengevouwen: een knop met de fase erin, op dezelfde plek en in dezelfde vorm als het volle paneel.
  Minimaliseren mag de run niet uit het zicht laten verdwijnen — dan is er geen weg terug naar de voortgang,
  en naar het afbreken evenmin. Meteen ook het duidelijkste bewijs dat minimaliseren de run laat doorlopen.
-->
<button
    v-if="planRunStore.showMinimizedProgress"
    class="plan-run-progress plan-run-progress--minimized"
    type="button"
    :aria-label="t('components.transport.dayplanner.plan_run.progress.restore')"
    :title="t('components.transport.dayplanner.plan_run.progress.restore')"
    @click="planRunStore.restoreProgressPanel()">
    <n-icon class="plan-run-progress__spinner">
        <SpinnerIos20Regular />
    </n-icon>
    <span class="plan-run-progress__minimized-label">{{ t(phaseLabelKey) }}</span>
    <n-icon>
        <ChevronUp16Regular />
    </n-icon>
</button>

<div v-else class="plan-run-progress" role="status">
    <div class="plan-run-progress__header">
        <span class="plan-run-progress__title">
            {{ t('components.transport.dayplanner.plan_run.progress.title') }}
        </span>
        <!--
          Inklappen, niet stoppen. Een chevron omlaag en geen kruisje: het paneel gaat niet weg, het wordt de
          pil hierboven, en die klapt met de chevron omhoog weer open. Een kruisje beloofde "weg ermee", en dat
          mag hier nooit een run van minuten kosten. Afbreken heeft daarom een eigen, benoemde knop onderin.
        -->
        <button
            class="plan-run-progress__collapse"
            type="button"
            :aria-label="t('components.transport.dayplanner.plan_run.progress.collapse_explained')"
            :title="t('components.transport.dayplanner.plan_run.progress.collapse_explained')"
            @click="planRunStore.dismissProgressPanel()">
            <n-icon>
                <ChevronDown16Regular />
            </n-icon>
        </button>
    </div>

    <n-progress
        :percentage="percentage"
        :show-indicator="false"
        processing
        type="line" />

    <div class="plan-run-progress__phase">{{ t(phaseLabelKey) }}</div>

    <!--
      Alleen bij de run van een collega. Wie zijn eigen planning volgt weet wie hem startte; voor iedereen
      anders verschijnt dit paneel uit het niets, en dan is de naam het eerste wat je wilt weten.
    -->
    <div v-if="startedByOtherPlanner" class="plan-run-progress__started-by">
        {{ t('components.transport.dayplanner.plan_run.progress.started_by',
              { name: planRunStore.activeRun?.createdByName }) }}
    </div>

    <!--
      Afbreken staat op de regel van de teller en niet in een eigen balk eronder: het paneel is een melding van
      vier regels, en een aparte actiebalk met scheidingslijn maakte er een dialoogje van.
      Alleen het stopicoon, want een tekstlabel maakte het paneel vol. Wel een volwaardige ronde knop en geen
      minuscuul tekstknopje: dit is de enige uitweg uit een run van minuten, en die moet je kunnen raken.
      Bewust ook zonder tooltip. De naam staat er wél als aria-label — anders zou een schermlezer alleen "knop"
      voorlezen. Dat het icoon voor een ziende gebruiker niet uitlegt wát er gebeurt is te verdedigen omdat
      erachter een bevestiging zit die de gevolgen uitschrijft: een misklik kost niets.
    -->
    <div class="plan-run-progress__footer">
        <!-- Loopt per seconde; een schermlezer hoort de fase, niet elke tik. -->
        <span class="plan-run-progress__elapsed" aria-hidden="true">{{ elapsedText }}</span>
        <!-- In de verwerkingsfase is afbreken te laat (de server weigert het dan ook); zie de store-getter. -->
        <tooltip-button
            :aria-label="planRunStore.activeRunIsApplying ? t(`${baseKey}.applying_title`) : t(`${baseKey}.action`)"
            :bordered="false"
            :disabled="planRunStore.cancelling || planRunStore.activeRunIsApplying"
            :icon="RecordStop16Regular"
            button-style="quaternary"
            html-classes="plan-run-progress__cancel"
            shape="circle"
            size="small"
            type="error"
            @click="showCancelConfirmation = true" />
    </div>

    <!--
      Dezelfde bevestiging als de rest van de app. Type Delete omdat er iets weggaat dat niet terugkomt, en
      "Laten doorlopen" in plaats van het standaard "Annuleren": annuleren zou in déze dialoog twee dingen
      kunnen betekenen.
    -->
    <confirmation-modal
        :visible="showCancelConfirmation"
        :title="t(`${baseKey}.confirm_title`)"
        :confirm-text="`${baseKey}.confirm`"
        :cancel-text="`${baseKey}.keep_running`"
        :dialog-type="DialogType.Delete"
        :loading="planRunStore.cancelling"
        @cancel="showCancelConfirmation = false"
        @confirm="cancel()">
        <!--
          Eén lopend verhaal in één tekstkleur, en niet drie losse regels waarvan er één grijs was. Die opmaak
          suggereerde een rangorde die er niet is: wat er stopt, wat er blijft staan en hoe je alsnog aan een
          planning komt horen bij elkaar en wegen even zwaar in de keuze. Het aantal trajecten zit in de tekst,
          dus de meervoudsvorm loopt mee.
        -->
        <template #content>
            <div>{{ t(`${baseKey}.confirm_description`, { count: trajectoryCount }, trajectoryCount) }}</div>
        </template>
    </confirmation-modal>
</div>
</template>

<style lang="scss" scoped>
/*
  Rechtsonder, boven de inhoud. Dezelfde vorm en schaduw als een notificatie: het paneel hoort tot diezelfde
  familie meldingen, en de afronding van de run komt straks ook als notificatie.
*/
.plan-run-progress {
    position: fixed;
    right: var(--dis-space-1_0x);
    bottom: var(--dis-space-1_0x);
    z-index: 10;
    box-sizing: border-box;
    width: 320px;
    padding: var(--dis-space-1_5x);
    border-radius: var(--dis-shape-radius-large);
    background-color: var(--dis-color-surface-primary);
    box-shadow: var(--dis-shadow-boxshadow3);
    animation: plan-run-progress-enter 240ms ease-out both;

    &__header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: var(--dis-space-1_0x);
        margin-bottom: var(--dis-space-1_0x);
    }

    &__title {
        color: var(--dis-color-text-body);
        font-size: var(--dis-font-size-h3);
        font-weight: var(--dis-font-weight-strong);
    }

    &__collapse {
        flex: 0 0 auto;
        width: var(--dis-shape-height-3_0x);
        height: var(--dis-shape-height-3_0x);
        margin: 0;
        padding: 0;
        border: 0;
        border-radius: var(--dis-shape-radius-medium);
        color: var(--dis-color-text-label);
        background-color: transparent;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;

        &:hover {
            color: var(--dis-color-text-body);
            background-color: var(--dis-color-surface-primary-hover);
        }
    }

    &__phase {
        margin-top: var(--dis-space-1_0x);
        color: var(--dis-color-text-body);
        font-size: var(--dis-font-size-medium);
    }

    /* Een terzijde onder de fase: dezelfde toon als de teller, en het mag inkorten in plaats van te wikkelen. */
    &__started-by {
        color: var(--dis-color-text-label);
        font-size: var(--dis-font-size-medium);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    &__footer {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: var(--dis-space-1_0x);
    }

    &__elapsed {
        color: var(--dis-color-text-label);
        font-size: var(--dis-font-size-medium);
        /*
          Mag inkorten in plaats van de knop weg te drukken: de fase erboven draagt de betekenis, en in het
          Duits is deze regel een stuk langer dan in het Nederlands.
        */
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    /*
      :deep() omdat de knop door TooltipButton gerenderd wordt en dus het scope-attribuut van dit bestand niet
      draagt; zonder dat grijpt de regel niet.

      Op één lijn met de inklapknop erboven. Beide knoppen lijnen rechts uit op dezelfde rand, maar deze is
      32px breed en die 24px, dus zonder correctie zit dit icoon (32-24)/2 = 4px verder naar links —
      zichtbaar scheef in een paneel van 320px. De knop schuift dus 4px op; zijn vlak loopt daarmee een stukje
      in de padding, wat niets uitmaakt omdat hij geen achtergrond heeft tot je erover gaat.
    */
    :deep(.plan-run-progress__cancel) {
        flex: 0 0 auto;
        margin-right: calc(-1 * var(--dis-space-0_5x));
    }

    /* Zelfde plek, zelfde vorm, alleen kleiner — zodat het als inklappen leest en niet als iets nieuws. */
    &--minimized {
        display: flex;
        align-items: center;
        gap: var(--dis-space-1_0x);
        width: auto;
        padding: var(--dis-space-1_0x) var(--dis-space-1_5x);
        border: 0;
        color: var(--dis-color-text-body);
        font-size: var(--dis-font-size-medium);
        cursor: pointer;

        &:hover {
            background-color: var(--dis-color-surface-primary-hover);
        }
    }

    &__spinner {
        color: var(--dis-color-text-label);
        animation: plan-run-progress-spin 1.4s linear infinite;
    }

    &__minimized-label {
        white-space: nowrap;
    }
}

@keyframes plan-run-progress-spin {
    to {
        transform: rotate(360deg);
    }
}

@keyframes plan-run-progress-enter {
    from {
        opacity: 0;
        transform: translateY(var(--dis-space-1_0x));
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}
</style>
